/**
 * ArticleCostOfLiving.tsx — /cost-of-living-brasov
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Same visual language as existing articles: Playfair Display headings, Source Sans 3 body
 * - Muted earth tones, generous whitespace, data tables for price reference
 * - Hero image from Piața Sfatului market, in-article supermarket image
 */

import { useState } from "react";
import { Clock, Share2, ChevronRight, MapPin, ExternalLink, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Article 4 hero: Piața Sfatului market scene with Black Church
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
// Article 4 in-article: Romanian supermarket interior
const SUPERMARKET_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-cost-living-supermarket-Gsn2nPG2BtzXSMEA6Mv3iz.webp";
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2" />
      <button type="submit" disabled={status === "loading"} className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" loading="lazy" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Price table component for consistent styling — fully responsive
function PriceTable({ rows }: { rows: { item: string; low: string; mid: string; high?: string }[] }) {
  const hasHigh = rows.some(r => r.high);
  return (
    <div className="my-6 w-full" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table
        className="border-collapse"
        style={{
          width: "100%",
          tableLayout: "fixed",
          fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)",
        }}
      >
        <colgroup>
          <col style={{ width: hasHigh ? "40%" : "50%" }} />
          <col style={{ width: hasHigh ? "20%" : "25%" }} />
          <col style={{ width: hasHigh ? "20%" : "25%" }} />
          {hasHigh && <col style={{ width: "20%" }} />}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
            <th
              className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200"
              style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}
            >Item</th>
            <th
              className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200"
              style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}
            >Budget</th>
            <th
              className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200"
              style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}
            >Mid</th>
            {hasHigh && (
              <th
                className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200"
                style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)", letterSpacing: "0.05em" }}
              >Premium</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
              <td
                className="text-stone-700 border-b border-stone-100"
                style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", lineHeight: 1.4, wordBreak: "break-word" }}
              >{row.item}</td>
              <td
                className="text-right text-stone-600 border-b border-stone-100 font-mono"
                style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
              >{row.low}</td>
              <td
                className="text-right text-stone-600 border-b border-stone-100 font-mono"
                style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
              >{row.mid}</td>
              {hasHigh && (
                <td
                  className="text-right text-stone-600 border-b border-stone-100 font-mono"
                  style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
                >{row.high || "—"}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Summary budget box
function BudgetSummary() {
  const profiles = [
    {
      label: "Single person (frugal)",
      items: [
        { name: "Rent (1BR, outside centre)", value: "€350–450" },
        { name: "Food & groceries", value: "€150–200" },
        { name: "Utilities (incl. internet)", value: "€80–120" },
        { name: "Transport (public)", value: "€25" },
        { name: "Healthcare (private sub.)", value: "€40–70" },
        { name: "Entertainment & misc.", value: "€100–150" },
      ],
      total: "€745–1,015 / month",
      color: "oklch(0.38 0.12 15)",
    },
    {
      label: "Single person (comfortable)",
      items: [
        { name: "Rent (1BR, central)", value: "€500–700" },
        { name: "Food & dining out", value: "€300–400" },
        { name: "Utilities (incl. internet)", value: "€100–150" },
        { name: "Transport (Bolt + car)", value: "€100–150" },
        { name: "Healthcare (private sub.)", value: "€70–100" },
        { name: "Entertainment & misc.", value: "€200–300" },
      ],
      total: "€1,270–1,800 / month",
      color: "oklch(0.38 0.12 15)",
    },
    {
      label: "Family of 3 (comfortable)",
      items: [
        { name: "Rent (2–3BR, good area)", value: "€700–1,000" },
        { name: "Food & dining out", value: "€500–700" },
        { name: "Utilities (incl. internet)", value: "€130–200" },
        { name: "Transport (car + public)", value: "€200–300" },
        { name: "Healthcare (family plan)", value: "€150–200" },
        { name: "School / childcare", value: "€300–500" },
        { name: "Entertainment & misc.", value: "€300–400" },
      ],
      total: "€2,280–3,300 / month",
      color: "oklch(0.38 0.12 15)",
    },
  ];

  return (
    <div className="my-10 grid grid-cols-1 md:grid-cols-3 gap-4">
      {profiles.map((profile, i) => (
        <div key={i} className="border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 text-white" style={{ backgroundColor: profile.color }}>
            <p className="text-xs uppercase tracking-widest font-semibold">{profile.label}</p>
          </div>
          <div className="p-4">
            {profile.items.map((item, j) => (
              <div key={j} className="flex justify-between items-start py-1.5 border-b border-stone-100 last:border-0">
                <span className="text-stone-600 text-xs leading-tight pr-2">{item.name}</span>
                <span className="text-stone-800 text-xs font-semibold whitespace-nowrap">{item.value}</span>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t-2 border-stone-300 flex justify-between items-center">
              <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Total</span>
              <span className="font-bold text-stone-900" style={{ fontFamily: "Playfair Display, serif", fontSize: "0.95rem" }}>{profile.total}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";

const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-hero-YwxKL9nvfxZqDmSm9YR8ym.webp";
const ARTICLE6_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";

const RELATED = [
  { tag: "Real Estate", title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing", img: ARTICLE6_IMG, href: "/cartier-transilvania-brasov" },
  { tag: "Lifestyle", title: "Entertainment in Brașov: Day & Night for Adults — with Real Prices", img: ARTICLE5_IMG, href: "/entertainment-brasov-guide" },
  { tag: "City Guide", title: "Brașov, straight up: beyond the bear photos and the mountain air clichés", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
];

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleCostOfLiving() {
  return (
    <>
    <Helmet>
      <title>Cost of Living in Brașov: Real Numbers for Expats (2026)</title>
      <meta name="description" content="Real cost of living in Brașov: rent, utilities, food, transport, and healthcare. Based on actual expat data, not tourist estimates. Updated 2026." />
      <meta name="keywords" content="cost of living Brasov, Brasov monthly expenses, rent in Brasov, Brasov expat budget, living costs Romania Brasov" />
      <meta property="og:title" content="Cost of Living in Brașov: Real Numbers for Expats (2026)" />
      <meta property="og:description" content="Real cost of living in Brașov: rent, utilities, food, transport, and healthcare. Based on actual expat data, not tourist estimates. Updated 2026." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/cost-of-living-brasov" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Cost of Living in Brașov: Real Numbers for Expats (2026)" />
      <link rel="canonical" href="https://brasovgateway.com/cost-of-living-brasov" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/cost-of-living-brasov#article",
              "headline": "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood",
              "description": "Detailed cost of living breakdown for Brașov, Romania — rent, groceries, utilities, transport, healthcare. Real data, no spin.",
              "image": "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp",
              "datePublished": "2026-04-03",
              "dateModified": "2026-04-03",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/cost-of-living-brasov",
              "inLanguage": "en",
              "articleSection": "Relocation"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Cost of Living in Brașov 2026: the honest numbers, neighbour...",
                  "item": "https://brasovgateway.com/cost-of-living-brasov"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Hero image */}
      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="Cost of living in Brașov — Piața Sfătului market" className="w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main content */}
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  Relocation
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>10 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                Cost of living in Brașov: the honest numbers, neighbourhood by neighbourhood
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Research &amp; Data Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/cost-of-living-brasov" title="Cost of Living in Brașov: Real Numbers for Expats (2026)" />
              </div>
            </div>

            {/* Article body */}
            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The cost of living in Brașov is one of the most searched questions among expats considering a move to Romania. Every few months someone posts in the expat Facebook groups asking exactly that. The answers range from "it's incredibly cheap" to "it's gotten expensive" — and both are technically correct, depending on how you choose to live. This article cuts through the noise with actual numbers, sourced from Numbeo, local real estate platforms, and the lived experience of someone who has been watching this city's prices for years.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                The short version: Brașov is no longer the bargain it was in 2018. Rents have roughly doubled in six years, property prices hit record highs in 2025, and grocery inflation has been persistent. But compared to Western Europe, you are still getting a very good deal — especially if you factor in the quality of life, the mountain access, and the fact that a good espresso still costs under a euro.
              </p>

              {/* Exchange rate note */}
              <div className="my-6 px-5 py-4 border-l-4" style={{ borderLeftColor: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.97 0.005 75)" }}>
                <p className="text-stone-600 text-sm font-light leading-relaxed">
                  <strong className="text-stone-800">Exchange rate note:</strong> All prices in this article are given in both Romanian leu (RON) and euros (EUR). We use an approximate rate of <strong>5 RON = 1 EUR</strong>, which has been broadly stable since Romania's EU accession. The exact rate fluctuates slightly; check cursbnr.ro for the current official BNR rate before making financial decisions.
                </p>
              </div>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Housing: the biggest variable in your budget
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Rent is where Brașov has changed most dramatically. In January 2026, the average asking price for apartments to buy hit <strong>2,228 EUR per square metre</strong> — making Brașov the second most expensive city in Romania, behind Cluj-Napoca and just ahead of Bucharest. That is a 9% increase year-on-year for new builds, and 8% for older stock. For renters, the picture is similar: demand from both locals and an influx of remote workers has pushed prices up steadily since 2021.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Here is what you can realistically expect to pay for a long-term rental in 2026, depending on the neighbourhood:
              </p>

              <PriceTable rows={[
                { item: "Studio / garsonieră (city-wide)", low: "250–300 €/mo", mid: "350–400 €/mo", high: "450+ €/mo" },
                { item: "1-bedroom, outside centre (Tractorul, Noua)", low: "350–420 €/mo", mid: "450–550 €/mo", high: "600+ €/mo" },
                { item: "1-bedroom, central (Centru Istoric, Schei)", low: "450–550 €/mo", mid: "600–750 €/mo", high: "800+ €/mo" },
                { item: "2-bedroom, outside centre", low: "500–600 €/mo", mid: "650–800 €/mo", high: "900+ €/mo" },
                { item: "2-bedroom, central", low: "650–800 €/mo", mid: "900–1,100 €/mo", high: "1,200+ €/mo" },
                { item: "3-bedroom, outside centre", low: "700–850 €/mo", mid: "900–1,100 €/mo", high: "1,200+ €/mo" },
                { item: "3-bedroom, central or Kasper / Tractorul / Coresi", low: "1,000–1,200 €/mo", mid: "1,300–1,600 €/mo", high: "1,800+ €/mo" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The most affordable areas for renters are <strong>Noua</strong> and <strong>Griviței</strong> (averaging around €350/month for a 1-bedroom). <strong>Tractorul</strong> and <strong>Coresi</strong> sit in the middle — modern buildings, good transport links, and rents that are roughly 20–30% below the historic centre. <strong>Centrul Istoric</strong> and the newer <strong>Kasper</strong> development command the highest prices in the city.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                One practical note: most landlords in Brașov ask for 1–2 months deposit plus the first month's rent upfront. Contracts are typically for one year, though shorter arrangements are negotiable. Furnished apartments are common and often preferred by expats — expect to pay a 10–15% premium over unfurnished equivalents.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Buying property: still worth it, but the window is narrowing
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                If you are considering buying rather than renting, the numbers look like this. For a deeper analysis of the market, developer selection, and the legal process, see our dedicated guide on <Link href="/buying-apartment-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>buying an apartment in Brașov in 2026</Link>.
              </p>

              <PriceTable rows={[
                { item: "Apartment, city centre (per m²)", low: "2,500 €/m²", mid: "3,000 €/m²", high: "3,500+ €/m²" },
                { item: "Apartment, outside centre (per m²)", low: "1,800 €/m²", mid: "2,200 €/m²", high: "2,600 €/m²" },
                { item: "1-bedroom apartment (40–50 m²)", low: "75,000 €", mid: "100,000 €", high: "130,000+ €" },
                { item: "2-bedroom apartment (60–75 m²)", low: "110,000 €", mid: "145,000 €", high: "190,000+ €" },
                { item: "3-bedroom apartment (85–100 m²)", low: "160,000 €", mid: "210,000 €", high: "280,000+ €" },
                { item: "House / villa (Brașov area)", low: "180,000 €", mid: "300,000 €", high: "600,000+ €" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Mortgage rates in Romania are currently around 7–8% annually for a 20-year fixed rate — significantly higher than the EU average, which makes buying less attractive than it was two years ago. That said, rental yields in Brașov remain strong at 5–7% gross, which explains why investor interest has not cooled despite higher financing costs.
              </p>

              <InArticleBanner />

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Food: where Brașov still delivers excellent value
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Groceries are where you will notice the biggest difference from Western Europe. Romania has a well-developed supermarket ecosystem — Lidl, Kaufland, Mega Image, Carrefour, and Penny are all present in Brașov — and prices for staples remain significantly lower than EU averages. Seasonal produce from local markets (Piața Centrală, Piața Tractorul) is even cheaper and notably fresher.
              </p>

              <figure className="my-10">
                <img src={SUPERMARKET_IMG} alt="Romanian supermarket with fresh produce section" className="w-full h-72 object-cover" loading="lazy" />
                <figcaption className="text-xs text-stone-400 mt-2 text-center">Romanian supermarkets like Lidl and Kaufland offer excellent value — fresh produce is particularly affordable compared to Western Europe</figcaption>
              </figure>

              <PriceTable rows={[
                { item: "Milk (1 litre)", low: "7 RON (1.4 €)", mid: "9 RON (1.8 €)", high: "12 RON (2.4 €)" },
                { item: "Bread (500g loaf)", low: "6 RON (1.2 €)", mid: "9 RON (1.8 €)", high: "12 RON (2.4 €)" },
                { item: "Eggs (12 large)", low: "12–14 RON (2.4–2.8 €)", mid: "15 RON (3 €)", high: "19 RON (3.8 €)" },
                { item: "Chicken breast (1 kg)", low: "25 RON (5 €)", mid: "31 RON (6.2 €)", high: "35 RON (7 €)" },
                { item: "Local cheese (1 kg)", low: "25–30 RON (5–6 €)", mid: "40 RON (8 €)", high: "50 RON (10 €)" },
                { item: "Tomatoes (1 kg)", low: "6–8 RON (1.2–1.6 €)", mid: "11 RON (2.2 €)", high: "16 RON (3.2 €)" },
                { item: "Potatoes (1 kg)", low: "3–4 RON (0.6–0.8 €)", mid: "4 RON (0.8 €)", high: "6 RON (1.2 €)" },
                { item: "Bottle of wine (mid-range)", low: "25 RON (5 €)", mid: "30 RON (6 €)", high: "50 RON (10 €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                A realistic monthly grocery budget for one person who cooks most meals at home is <strong>€150–200</strong>. A couple can eat well for <strong>€250–350</strong>. If you shop at local markets and buy seasonal produce, you can do it for less; if you gravitate toward imported products and specialty stores, expect to pay more.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Eating out: from 12 lei to 350 lei per head
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov has a genuinely good restaurant scene — better than you might expect for a city of 300,000. The historic centre is dense with options ranging from traditional Romanian taverns to modern European bistros. Prices vary accordingly.
              </p>

              <PriceTable rows={[
                { item: "Meal at a cheap local restaurant", low: "40–50 RON (8–10 €)", mid: "60 RON (12 €)", high: "—" },
                { item: "3-course meal, mid-range restaurant (per person)", low: "80–100 RON (16–20 €)", mid: "125 RON (25 €)", high: "175 RON (35 €)" },
                { item: "Cappuccino / espresso", low: "14–16 RON (2.8–3.2 €)", mid: "16 RON (3.2 €)", high: "20 RON (4 €)" },
                { item: "Draft beer (0.5L)", low: "10–12 RON (2–2.4 €)", mid: "15 RON (3 €)", high: "20 RON (4 €)" },
                { item: "Fast food combo (McDonald's / KFC)", low: "30–35 RON (6–7 €)", mid: "35 RON (7 €)", high: "40 RON (8 €)" },
                { item: "Pizza (restaurant, medium)", low: "40–50 RON (8–10 €)", mid: "55 RON (11 €)", high: "70 RON (14 €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Tipping culture in Brașov is evolving. A 10% tip is standard and appreciated; in higher-end restaurants, 15% is becoming more common. Food delivery (Glovo, Tazz, Bolt Food) adds a delivery fee of around 10–15 RON per order and is widely used by locals.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Utilities: the winter heating question
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Utility costs in Brașov have one significant variable: heating. The city sits at 600m altitude and winters are real — temperatures regularly drop to -10°C or below. How you heat your apartment matters a lot for your monthly bill.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                Older buildings in the city centre are often connected to the district heating network (termoficare), which provides central heating at a regulated tariff. Newer buildings typically have individual gas boilers or heat pumps. Electric heating is less common but exists in some studio apartments.
              </p>

              <PriceTable rows={[
                { item: "Electricity (85m² apartment, monthly)", low: "150–200 RON (30–40 €)", mid: "250 RON (50 €)", high: "400 RON (80 €)" },
                { item: "Gas heating (winter months, 85m²)", low: "300–400 RON (60–80 €)", mid: "500 RON (100 €)", high: "700 RON (140 €)" },
                { item: "District heating (termoficare, winter)", low: "200–300 RON (40–60 €)", mid: "350 RON (70 €)", high: "500 RON (100 €)" },
                { item: "Water & sewage (monthly)", low: "50–70 RON (10–14 €)", mid: "80 RON (16 €)", high: "120 RON (24 €)" },
                { item: "Broadband internet (100+ Mbps)", low: "30–40 RON (6–8 €)", mid: "40 RON (8 €)", high: "60 RON (12 €)" },
                { item: "Mobile plan (with 10GB+ data)", low: "20–30 RON (4–6 €)", mid: "38 RON (7.6 €)", high: "80 RON (16 €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                A realistic all-in utility budget for a single person in an 85m² apartment is <strong>€80–150/month in summer</strong> and <strong>€150–250/month in winter</strong>. Internet in Romania is genuinely excellent — among the fastest in Europe — and very affordable. Mobile plans are similarly cheap: for €8–15/month you get unlimited calls and substantial data.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Transport: the city is walkable, but a car helps
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov's historic centre is compact and highly walkable. For everything else, you have options. The public transport network (RATBV) covers the city reasonably well with buses and a few trolleybus lines. From May 2025, RATBV introduced a new distance-based fare structure:
              </p>

              <PriceTable rows={[
                { item: "Single bus ticket (urban zone)", low: "5 RON (1 €)", mid: "5 RON (1 €)", high: "—" },
                { item: "Monthly pass (urban, unlimited)", low: "110 RON (22 €)", mid: "110 RON (22 €)", high: "—" },
                { item: "Monthly pass (all zones, incl. metropolitan)", low: "300 RON (60 €)", mid: "300 RON (60 €)", high: "—" },
                { item: "Bolt / Uber ride (short, within city)", low: "15–25 RON (3–5 €)", mid: "25–35 RON (5–7 €)", high: "—" },
                { item: "Traditional taxi (per km)", low: "3.10 RON (0.62 €)", mid: "3.50 RON (0.70 €)", high: "—" },
                { item: "Fuel / petrol (per litre)", low: "7.00 RON (1.40 €)", mid: "7.43 RON (1.49 €)", high: "8.00 RON (1.60 €)" },
                { item: "Car insurance (annual, mid-size car)", low: "1,200 RON (240 €)", mid: "1,800 RON (360 €)", high: "3,000 RON (600 €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                If you live in the centre and work remotely, you can easily get by without a car — especially with Bolt and Uber both operating in the city. If you have children, commute to an industrial area, or want to explore the surrounding mountains and villages regularly, a car becomes almost essential. Parking in the centre is increasingly regulated and paid; outside the centre it is generally free.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                <strong>A note on fuel prices:</strong> Petrol and diesel prices in Romania have risen following the conflict in Iran and its impact on global oil markets. The Romanian government has responded by reducing fuel excise duties to keep pump prices at an acceptable level for consumers — a policy that has partially offset the global price increase. Prices remain among the more competitive in the EU, but are no longer as low as they were in 2023–2024. Monitor peco-online.ro for current station-by-station prices in Brașov.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Healthcare: public is free, private is affordable
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Romania has a public healthcare system (CNAS) that is free for employed residents and EU citizens who register. The reality is that most expats — and many locals — prefer private healthcare for routine care, due to shorter waiting times and English-speaking staff. Brașov has several good private networks: <strong>Regina Maria</strong>, <strong>MedLife</strong>, and the local <strong>Clinicco</strong> hospital are the main options.
              </p>

              <PriceTable rows={[
                { item: "GP consultation (private, per visit)", low: "100–150 RON (20–30 €)", mid: "150 RON (30 €)", high: "250 RON (50 €)" },
                { item: "Specialist consultation (private)", low: "200–300 RON (40–60 €)", mid: "300 RON (60 €)", high: "500 RON (100 €)" },
                { item: "Private health subscription (Regina Maria / MedLife)", low: "200–300 RON/mo (40–60 €)", mid: "350 RON/mo (70 €)", high: "500+ RON/mo (100+ €)" },
                { item: "Dental cleaning (private)", low: "150–200 RON (30–40 €)", mid: "200 RON (40 €)", high: "350 RON (70 €)" },
                { item: "Prescription medication (generic)", low: "20–50 RON (4–10 €)", mid: "50 RON (10 €)", high: "150 RON (30 €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                A private healthcare subscription (abonament medical) with Regina Maria or MedLife typically costs €40–100/month depending on coverage level and gives you access to GP visits, basic specialist consultations, and some diagnostic tests without additional charges. For expats who are not employed in Romania and therefore not contributing to the state system, this is the recommended route.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Entertainment & lifestyle: surprisingly affordable
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                This is where Brașov genuinely shines for quality-of-life value. The city has a thriving cultural scene — opera, theatre, film festivals — and the surrounding mountains offer world-class hiking, skiing, and cycling essentially for free (or very cheaply). For a full map of what to do and where to go, with real prices, read our <Link href="/entertainment-brasov-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>entertainment guide for Brașov</Link>.
              </p>

              <PriceTable rows={[
                { item: "Cinema ticket (international release)", low: "32 RON (6.4 €)", mid: "45 RON (9 €)", high: "61 RON (12.2 €)" },
                { item: "Gym membership (monthly)", low: "150–200 RON (30–40 €)", mid: "280 RON (56 €)", high: "400 RON (80 €)" },
                { item: "Ski day pass (Poiana Brașov)", low: "120 RON (24 €)", mid: "160 RON (32 €)", high: "200 RON (40 €)" },
                { item: "Ski season pass (Poiana Brașov)", low: "1,800 RON (360 €)", mid: "2,200 RON (440 €)", high: "—" },
                { item: "Theatre / opera ticket", low: "30–50 RON (6–10 €)", mid: "70 RON (14 €)", high: "150 RON (30 €)" },
                { item: "Hiking (trails, national parks)", low: "Free – 20 RON (0–4 €)", mid: "—", high: "—" },
                { item: "Haircut (barber / hairdresser)", low: "40–60 RON (8–12 €)", mid: "80 RON (16 €)", high: "150+ RON (30+ €)" },
              ]} />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The Poiana Brașov ski resort is a 15–30 minute drive from the city centre — one of the most convenient ski resorts in Europe relative to an urban centre. In summer, the same mountains offer hundreds of kilometres of marked hiking trails. The Brașov Philharmonic, Opera, and Dramatic Theatre all offer high-quality performances at prices that would be considered absurdly cheap in Western Europe.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Education & childcare
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                For families with children, education costs are an important variable. Public schools are free but teach in Romanian. Private and international options are available but represent a significant budget line.
              </p>

              <PriceTable rows={[
                { item: "Private kindergarten / preschool (monthly)", low: "1,500 RON (300 €)", mid: "2,000 RON (400 €)", high: "2,500 RON (500 €)" },
                { item: "International school (annual tuition)", low: "15,000 RON (3,000 €)", mid: "25,000 RON (5,000 €)", high: "45,000 RON (9,000 €)" },
                { item: "Private Romanian school (annual)", low: "8,000 RON (1,600 €)", mid: "12,000 RON (2,400 €)", high: "—" },
                { item: "After-school activities (monthly)", low: "100–200 RON (20–40 €)", mid: "300 RON (60 €)", high: "500 RON (100 €)" },
              ]} />

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                What does it all add up to? Monthly budget scenarios
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Here are three realistic monthly budget scenarios for living in Brașov in 2026. These are estimates based on the data above and real-world feedback from expats currently living in the city.
              </p>

              <BudgetSummary />

              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                For context: the average net salary in Brașov in mid-2025 was approximately <strong>5,376 RON (around €1,075/month)</strong>. This means that many locals live on budgets significantly tighter than the "comfortable single" scenario above — and they manage. As an expat with a Western income or remote work arrangement, you will likely find Brașov very affordable by comparison.
              </p>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                How Brașov compares to other cities
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                To put these numbers in perspective, here is how Brașov's cost of living compares to other popular expat destinations in the region:
              </p>

              <div className="my-6 w-full" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table
                  className="border-collapse"
                  style={{ width: "100%", tableLayout: "fixed", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}
                >
                  <colgroup>
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "22%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                      <th className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)" }}>City</th>
                      <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)" }}>1BR rent</th>
                      <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)" }}>Meal</th>
                      <th className="text-right text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", fontSize: "clamp(0.6rem, 1.8vw, 0.75rem)" }}>Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { city: "Brașov, Romania", rent: "€500–700", meal: "€20–30", monthly: "€1,000–1,500" },
                      { city: "Bucharest, Romania", rent: "€600–900", meal: "€25–35", monthly: "€1,100–1,700" },
                      { city: "Cluj-Napoca, Romania", rent: "€650–950", meal: "€25–35", monthly: "€1,200–1,800" },
                      { city: "Sofia, Bulgaria", rent: "€500–700", meal: "€20–30", monthly: "€1,000–1,400" },
                      { city: "Budapest, Hungary", rent: "€700–1,100", meal: "€25–40", monthly: "€1,400–2,000" },
                      { city: "Prague, Czech Rep.", rent: "€900–1,400", meal: "€30–50", monthly: "€1,800–2,600" },
                      { city: "Lisbon, Portugal", rent: "€1,200–1,800", meal: "€35–55", monthly: "€2,200–3,200" },
                      { city: "Barcelona, Spain", rent: "€1,400–2,200", meal: "€40–70", monthly: "€2,500–4,000" },
                    ].map((row, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                        <td
                          className={`border-b border-stone-100 ${row.city.includes("Brașov") ? "font-semibold text-stone-900" : "text-stone-700"}`}
                          style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
                        >{row.city}</td>
                        <td
                          className={`text-right border-b border-stone-100 font-mono ${row.city.includes("Brașov") ? "font-semibold text-stone-900" : "text-stone-600"}`}
                          style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
                        >{row.rent}</td>
                        <td
                          className={`text-right border-b border-stone-100 font-mono ${row.city.includes("Brașov") ? "font-semibold text-stone-900" : "text-stone-600"}`}
                          style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
                        >{row.meal}</td>
                        <td
                          className={`text-right border-b border-stone-100 font-mono ${row.city.includes("Brașov") ? "font-semibold text-stone-900" : "text-stone-600"}`}
                          style={{ padding: "clamp(0.4rem, 1.5vw, 0.75rem) clamp(0.5rem, 2vw, 1rem)", wordBreak: "break-word", lineHeight: 1.4 }}
                        >{row.monthly}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The bottom line
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Brașov in 2026 is not the ultra-cheap destination it was five years ago. Rents have risen sharply, property prices are at record highs, and grocery inflation has been persistent. But the city still offers a compelling value proposition for expats and remote workers: a high quality of life, genuine mountain access, a functioning city with good infrastructure, and costs that are 40–60% lower than comparable Western European cities.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The sweet spot is a comfortable single-person budget of around <strong>€1,200–1,500/month</strong>, which buys you a decent central apartment, good food, occasional dining out, private healthcare, and enough left over for weekend ski trips or hiking. For a couple, <strong>€2,000–2,500/month</strong> covers a comfortable life without counting every penny.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                If you are thinking about making the move, <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>read our honest guide for expats first</Link> — and then the best thing you can do is spend a month here — rent a short-term apartment, shop at the local supermarkets, take the bus, have coffee in the square. The numbers in this article will make more sense once you have lived them.
              </p>

              {/* CTA */}
              <div className="mt-12 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest text-stone-400 mb-3">Planning your move?</p>
                <h3 className="text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.4rem", fontWeight: 700 }}>
                  We can help you find the right apartment and navigate the paperwork
                </h3>
                <p className="text-stone-600 font-light leading-relaxed mb-5 text-sm">
                  From neighbourhood selection to lease negotiation, residence registration, and connecting with local services — Brașov Gateway offers hands-on relocation support for expats and foreign investors.
                </p>
                <Link href="/contact">
                  <button
                    className="flex items-center gap-2 text-xs text-white px-6 py-3 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                    GET IN TOUCH <ChevronRight size={12} />
                  </button>
                </Link>
              </div>
            </div>

            {/* Related articles */}
            <div className="mt-16 pt-10 border-t border-stone-200">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-6">Continue reading</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {RELATED.map((article, i) => (
                  <Link key={i} href={article.href}>
                    <div className="group cursor-pointer">
                      <div className="overflow-hidden mb-3">
                        <img
                          src={article.img}
                          alt={article.title}
                          className="w-full h-36 object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "oklch(0.38 0.12 15)" }}>
                        {article.tag}
                      </p>
                      <h4 className="text-stone-800 text-sm font-semibold leading-snug group-hover:text-stone-600 transition-colors" style={{ fontFamily: "Playfair Display, serif" }}>
                        {article.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              <SidebarAd />

              {/* Quick reference box */}
              <div className="border border-stone-200 p-5" style={{ backgroundColor: "white" }}>
                <p className="text-xs uppercase tracking-widest text-stone-400 mb-4">Quick reference</p>
                <div className="space-y-3">
                  {[
                    { label: "1BR rent (outside centre)", value: "€350–550/mo" },
                    { label: "1BR rent (central)", value: "€500–750/mo" },
                    { label: "Groceries (1 person)", value: "€150–200/mo" },
                    { label: "Utilities (incl. internet)", value: "€80–150/mo" },
                    { label: "Monthly bus pass", value: "€22/mo" },
                    { label: "Private health sub.", value: "€40–100/mo" },
                    { label: "Gym membership", value: "€30–60/mo" },
                    { label: "Average local salary", value: "~€1,075 net" },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-start border-b border-stone-100 pb-2 last:border-0 last:pb-0">
                      <span className="text-stone-500 text-xs leading-tight pr-2">{item.label}</span>
                      <span className="text-stone-800 text-xs font-semibold whitespace-nowrap">{item.value}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-stone-400 mt-4 leading-relaxed">
                  Data: Numbeo, Imobiliare.ro, RATBV, INSSE Brașov. Updated April 2026.
                </p>
              </div>
              {/* Newsletter */}
              <div className="border border-stone-200 p-5">
                <h4 className="font-bold text-stone-900 mb-2 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>Stay in the loop</h4>
                <p className="text-stone-500 text-xs font-light leading-relaxed mb-3">No spam. One email per week with what is actually happening in Brașov.</p>
                <NewsletterInlineLight />
              </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
